import styled from 'styled-components';
import { UrlObject } from 'url';
import { Trans } from '@lingui/react';
import { parse } from 'date-fns/parse';
import { isFuture } from 'date-fns/isFuture';
import { Box, Container } from '@components/layout/Grid';
import { BundleQuery } from '@gql/generated';
import {
  Title,
  SubTitle,
  CategoryTitle,
  VideoWrapper,
  TextWrapperInner,
} from '@ecosystems/landing_pages/hero/components';
import Hero, { Thumbnail } from '@ecosystems/landing_pages/hero/Hero';
import { hexToRgbA } from '@lib/utils';
import { BundleAccessState } from '@pages/[lang]/c/[client]/b/[slug]';
import { JoinBundleWaitlist } from '@ecosystems/buttons/JoinBundleWaitlist';
import { Button } from '@components/ButtonV2';

const Wrapper = styled(Container)`
  // background-color: #f7f7f7;
`;

const Section = styled(Hero)`
  width: 100%;
  padding: 0;

  ${CategoryTitle} {
    position: relative;
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    line-height: 17px;
    margin-top: 20px;
    margin-bottom: 15px;

    svg {
      margin-right: 5px;
    }
    i {
      margin-left: 5px;
      cursor: pointer;
    }
  }

  ${Title} {
    font-size: 24px;
    color: #242424;
  }

  ${SubTitle} {
    max-width: 500px;
    font-size: 14px;
    line-height: 22px;
  }

  ${Thumbnail} {
    height: auto;
    aspect-ratio: 3 / 2;
    object-fit: cover;
  }

  ${VideoWrapper} {
    display: flex;
    align-items: center;
    border-radius: 5px;
    overflow: hidden;
  }

  @media (min-width: ${({ theme }) => theme.viewports.laptop}px) {
    ${TextWrapperInner} {
      max-width: 500px;
    }

    ${VideoWrapper} {
      padding: 0 0 0 40px;
    }
  }
`;

const GetAccessButton = styled(Button)`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.secondary.blue};

  :hover {
    background-color: ${({ theme }) =>
      hexToRgbA(theme.colors.secondary.blue, 0.9)};
  }

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    width: auto;
  }
`;

type Props = {
  bundle: BundleQuery['bundle'];
  getStartedUrl: UrlObject;
  userAccessState: BundleAccessState;
};

export const InfoPanel1Section = (props: Props) => {
  const { bundle, getStartedUrl, userAccessState } = props;

  if (!bundle) {
    return null;
  }

  let actions = [];
  if (bundle?.waitingItemInfo?.waitingListEnabled) {
    const startDate = bundle?.waitingItemInfo?.startDate
      ? parse(bundle?.waitingItemInfo?.startDate, 'yyyy-MM-dd', new Date())
      : null;

    if (isFuture(startDate) && !bundle?.waitingItemInfo?.waitingListJoined) {
      actions = [
        <JoinBundleWaitlist
          bundle={{
            id: bundle?.id,
            title: bundle?.title,
            slug: bundle?.slug,
            client_name: bundle?.client?.name,
            bundleCategory: bundle?.bundleCategory,
          }}
        />,
      ];
    }
  } else if (
    bundle?.infoPanel?.buttonLink &&
    bundle?.infoPanel?.buttonContent
  ) {
    actions = [
      <GetAccessButton key="0" href={bundle?.infoPanel?.buttonLink}>
        {bundle?.infoPanel?.buttonContent}
      </GetAccessButton>,
    ];
  } else {
    actions =
      userAccessState === BundleAccessState.Guest
        ? [
            <GetAccessButton key="0" href={getStartedUrl}>
              <Trans id="bundle.id_page.hero.get_access_btn" />
            </GetAccessButton>,
          ]
        : [];
  }

  return (
    <Wrapper py={40} px={20} fullWidth>
      <Container>
        <Section
          titleAs="h2"
          image={bundle?.infoPanel?.image || bundle.heroImage}
          title={bundle.infoPanel?.title ?? bundle.title}
          description={
            <span
              dangerouslySetInnerHTML={{
                __html:
                  bundle.infoPanel?.description ?? bundle.shortDescription,
              }}
            ></span>
          }
          actions={
            actions?.length > 0
              ? [
                  <Box key="1" my={20}>
                    {actions}
                  </Box>,
                ]
              : []
          }
          reverseColumns
        />
      </Container>
    </Wrapper>
  );
};
